import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ORDER_TYPE } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { useMockCustomer } from "./useMockCustomer";
import { checkStock, clearCustomerCart, finalizeSale, priceLines, readCustomerCart, resolveBranchId } from "../../services/commerce";
import PaymentQrModal from "../../components/PaymentQrModal";
import { Button, Card, EmptyState, Select, Skeleton, baht, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function Checkout() {
  const navigate = useNavigate();
  const { orgId, organization, branchId, branches, toast } = useApp();
  const { tx } = useI18n();
  const { customer } = useMockCustomer(orgId);
  const [rows, setRows] = useState(() => readCustomerCart(orgId));
  const [cartReady, setCartReady] = useState(!!orgId);
  // orgId อาจยังว่างตอนรีเฟรช -> อ่านตะกร้าใหม่เมื่อ orgId พร้อม
  useEffect(() => {
    if (!orgId) return;
    setRows(readCustomerCart(orgId));
    setCartReady(true);
  }, [orgId]);
  const [pickedBranch, setPickedBranch] = useState("");
  const [quote, setQuote] = useState(null); // ราคาที่คิดใหม่จากระบบ ณ ตอนขึ้น QR — ยอดที่จ่าย = ยอดที่บันทึก
  const [checking, setChecking] = useState(false);
  const [showQr, setShowQr] = useState(false);

  // ราคาคิดใหม่จากสินค้าในระบบ ไม่เชื่อราคาที่ client ถือ
  const { data: priced, loading } = useAsync(() => priceLines(orgId, rows), [orgId, rows], { lines: [], total: 0, missing: [] });
  const saleBranchId = pickedBranch || resolveBranchId(branches, branchId);

  if (!cartReady) return <Skeleton height={160} />;
  if (rows.length === 0) return <EmptyState icon={<Icon name="cart" />} text={tx("ไม่มีสินค้าที่จะชำระเงิน", "Nothing to check out")} />;
  if (loading) return <Skeleton height={160} />;
  if (!priced.lines.length) return <EmptyState icon={<Icon name="cart" />} text={tx("สินค้าในตะกร้าไม่พร้อมขายแล้ว", "Items in your cart are no longer available")} />;

  const startPay = async () => {
    if (checking) return;
    if (!saleBranchId) return toast(tx("กรุณาเลือกสาขา", "Please select a branch"), "error");
    setChecking(true);
    try {
      const fresh = await priceLines(orgId, rows);
      if (fresh.missing.length || !fresh.lines.length) return toast(tx("มีสินค้าที่ปิดขายแล้ว กรุณากลับไปตรวจตะกร้า", "Some items are unavailable — please review your cart"), "error");
      const short = await checkStock(orgId, saleBranchId, fresh.lines);
      if (short.length) {
        return toast(tx(`สินค้าไม่พอ: ${short.map((row) => `${row.name} (เหลือ ${row.have})`).join(", ")}`, `Not enough stock: ${short.map((row) => `${row.name} (${row.have} left)`).join(", ")}`), "error");
      }
      setQuote(fresh);
      setShowQr(true);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setChecking(false);
    }
  };

  const onPaid = async (transaction) => {
    const result = await finalizeSale({ orgId, organization, kind: "checkout", lines: quote.lines, branchId: saleBranchId, customer, transaction });
    clearCustomerCart(orgId);
    toast(tx(`สั่งซื้อและชำระเงินสำเร็จ · ใบเสร็จ ${result.receipt.docNo}`, `Order placed and paid · receipt ${result.receipt.docNo}`), "success");
    navigate("/app/billing");
  };

  return (
    <>
      <Card title={tx("สรุปคำสั่งซื้อ", "Order summary")}>
        {priced.lines.map((row) => (
          <div key={row.productId} className="u-between u-fs-13" style={{ padding: "6px 0" }}>
            <span>{row.name} x{row.quantity}</span>
            <span>{baht(row.total)}</span>
          </div>
        ))}
        <div className="u-between u-mt-16" style={{ fontSize: 18, fontWeight: 800 }}>
          <span>{tx("ยอดสุทธิ", "Total")}</span>
          <span>{baht(priced.total)}</span>
        </div>
      </Card>

      {branches.length > 1 ? (
        <Card>
          <Select
            label={tx("สาขาที่รับสินค้า", "Branch")}
            placeholder={tx("เลือกสาขา", "Select branch")}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
            value={saleBranchId}
            onChange={(e) => setPickedBranch(e.target.value)}
          />
        </Card>
      ) : null}

      <Button variant="primary" block disabled={checking} onClick={startPay}>
        {tx("ชำระด้วย QR", "Pay with QR")}
      </Button>

      <PaymentQrModal
        open={showQr && !!quote}
        title={tx("ชำระค่าสินค้า", "Checkout payment")}
        amount={quote?.total || 0}
        orderType={ORDER_TYPE.checkout}
        items={(quote?.lines || []).map((row) => ({ productName: row.name, productCode: row.productId, quantity: row.quantity, price: row.price }))}
        onClose={() => setShowQr(false)}
        onPaid={onPaid}
      />
    </>
  );
}
